import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { api, friendlyError, unwrap } from '@/lib/api';
import { ACCOUNT } from '@/constants/testIds';
import { FavoriteClubsBlock } from '@/components/clubs/FavoriteClubsBlock';

export default function AccountSettings() {
  const { user, fetchMe } = useAuth();
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    marketing_consent: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/users/me');
        const data = unwrap(res);
        setForm({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          marketing_consent: !!data.marketing_consent,
        });
      } catch (err) {
        setError(friendlyError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const res = await api.patch('/users/me', {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        marketing_consent: !!form.marketing_consent,
      });
      setMessage(res?.data?.message || 'Datos actualizados.');
      await fetchMe();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <section className="py-10 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-[11px] uppercase tracking-[0.25em] text-[var(--ns-gold-600)] font-semibold">
            Mi cuenta
          </span>
          <h1 className="font-display uppercase tracking-tight text-[var(--ns-text)] text-3xl sm:text-5xl mt-2 leading-[1.02]">
            Datos personales
          </h1>
          <p className="text-sm text-[var(--ns-text-muted)] mt-2">
            Actualiza tu información básica. El email y el tipo de perfil solo pueden ser modificados por un administrador.
          </p>

          <Card className="mt-6 bg-white border border-[var(--ns-border)] rounded-2xl shadow-[var(--ns-shadow-soft)]">
            <CardContent className="p-6 sm:p-8">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[var(--ns-gold-600)]" />
                </div>
              ) : (
                <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="first_name" className="text-sm font-medium">Nombre</Label>
                      <Input
                        id="first_name"
                        required
                        value={form.first_name}
                        onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                        data-testid={ACCOUNT.firstName}
                        className="h-11 bg-white"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor="last_name" className="text-sm font-medium">Apellidos</Label>
                      <Input
                        id="last_name"
                        required
                        value={form.last_name}
                        onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
                        data-testid={ACCOUNT.lastName}
                        className="h-11 bg-white"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label className="text-sm font-medium">Email</Label>
                    <Input
                      value={user?.email || ''}
                      disabled
                      className="h-11 bg-[var(--ns-bg-2)]"
                    />
                  </div>

                  <label className="flex items-start gap-2 text-sm text-[var(--ns-text)] cursor-pointer">
                    <Checkbox
                      checked={form.marketing_consent}
                      onCheckedChange={(v) => setForm((f) => ({ ...f, marketing_consent: !!v }))}
                      data-testid={ACCOUNT.marketing}
                      className="mt-0.5"
                    />
                    Quiero recibir novedades, eventos y promociones por email.
                  </label>

                  {message && (
                    <Alert
                      data-testid={ACCOUNT.successMessage}
                      className="border-[var(--ns-green)]/40 bg-[var(--ns-green)]/8"
                    >
                      <AlertDescription className="text-[var(--ns-green-700)]">{message}</AlertDescription>
                    </Alert>
                  )}
                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    disabled={saving}
                    data-testid={ACCOUNT.submit}
                    className="h-11 rounded-xl bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white hover:shadow-[var(--ns-glow-gold)] border-0 shadow-[var(--ns-shadow-gold)] transition-all"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar cambios'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>

          <div className="mt-6">
            <FavoriteClubsBlock onChanged={fetchMe} />
          </div>
        </div>
      </section>
    </MainLayout>
  );
}
